import React, { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { fetchData } from "../utils/api"
import { useSearchParams } from "react-router-dom";

import Header from '../components/marketing/header.js'
import ChannelCard from '../components/marketing/channels.js'
import Loader from "../components/Loader.js";
import useFeatureGates from "../hooks/useFeatureGates";
import { isPressReleaseV2Enabled } from "../utils/pressReleaseAuth";

import { MARKETING_CHANNELS_ARRAY } from "../config/marketingConfig";

// Channels that are never gated (always accessible)
const NON_GATED_SLUGS = ['event_calendars', 'featured_placements', 'email_list'];

// Legacy Google Ads AI standalone workflow is being retired in favor of the
// agent/agent-jobs path (Billy decision 2026-07-23). Hidden from non-admins;
// platform admins keep full access so the feature stays live-testable.
// Reversible: flip this list back to [] (or delete the filter) to restore.
const ADMIN_ONLY_SLUGS = ['google_ads_ai'];

const MarketingBoostsPage = () => {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("event_id");

  // Mirrors the pattern in ImportsPage.jsx — `is-admin` is set as an
  // attribute on #react-root by marketing/index.html.haml based on
  // current_user.admin?. Read-only here; the server-side guard in
  // marketing_controller.rb is the actual security boundary.
  const [isAdmin] = useState(() => {
    const root = typeof document !== "undefined" ? document.getElementById("react-root") : null
    return root ? root.getAttribute("is-admin") === "true" : false
  })

  // Press Release v2 (company FeatureGate) — flips the press_release_ai card to the external app.
  const { featureGates } = useFeatureGates('company');
  const pressReleaseV2 = isPressReleaseV2Enabled(featureGates);

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ["marketingBoosts", eventId],
    queryFn: () => fetchData("api/marketing", { event_id:  eventId})
  })

  if (isLoading || isFetching) return (
    <Loader />
  )

  const channelAccess = data?.channel_access || {};

  const visibleChannels = isAdmin
    ? MARKETING_CHANNELS_ARRAY
    : MARKETING_CHANNELS_ARRAY.filter(channel => !ADMIN_ONLY_SLUGS.includes(channel.slug));

  const channels = visibleChannels.map(channel => {
    // Non-gated channels always have access
    if (NON_GATED_SLUGS.includes(channel.slug)) return channel;
    // For gated channels, check channel_access from API
    const hasAccess = channelAccess[channel.slug] === true;
    return { ...channel, hasAccess };
  });

  const isChannelBoosted = (channel) => {
    if (!data?.event || Object.keys(data.event).length === 0) return false;

    if (channel?.slug === 'event_calendars') {
      return data.event.published;
    } else if (channel?.slug === 'featured_placements') {
      return (!data.event.published || data?.event?.is_event_boosted)
    } else if (channel?.slug === 'email_list') {
      return (!data.event.published || channel?.implementations?.some(i => data?.event?.purchased_boost_types?.includes(i)));
    } else {
      return false
    }
  };

  return (
    <div className="marketing">
      <Header activeIndex={0} />

      <div className="marketing__content cts-container">
          {Object.keys(data?.event).length > 0 && (
            <div className="marketing__content__title">
              <img src={data?.event?.banner_image} alt={data?.event?.name} width="124" height="62" />
              <h1>{data?.event?.name}</h1>
            </div>
          )}

          <div className="row g-4">
            {channels.map((channel, index) => (
              <div className="col-12 col-sm-6 col-md-6 col-lg-4" key={index}>
                <ChannelCard boostingEventId={eventId} eventId={channel?.slug === 'event_calendars' && data?.event?.id} channel={channel} isBoosted={isChannelBoosted(channel)} companySlug={data?.company_slug} upsellAvailable={Object.keys(data?.event).length > 0 ? channel.implementations.some(i => data?.channels?.some(c => c?.slug === i && c.upsell_available)) : true} isPublished={data?.event?.published} isInPast={data?.event?.is_in_past} hasAccess={channel.hasAccess !== false} pressReleaseV2={pressReleaseV2} />
              </div>
            ))}
          </div>

      </div>
    </div>
  );
}

export default MarketingBoostsPage
